import { UserProfile } from "@/types/dashboard";
import { supabase } from "../supabase";
import { clearAccessToken, getAccessToken } from "./accessTokenStore";
import { reportError } from "@/integrations/sentry";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.omnara.com";

export interface SignUpData {
  email: string;
  password: string;
}

export interface SignUpResponse {
  user: UserProfile | null;
  message: string;
}

export interface SignInData {
  email: string;
  password: string;
}

export interface AuthResponse {
  user: UserProfile;
  message: string;
}

// Centralized local auth cleanup exported for reuse
export function clearLocalAuth(): void {
  try {
    // App-specific token (legacy/custom)
    try {
      localStorage.removeItem("authToken");
    } catch {
      // Silently ignore if item doesn't exist
    }

    // Supabase client keys that may linger locally
    const patterns: RegExp[] = [
      /^sb-.*-auth-token$/i, // Primary DB auth token
      /^sb-.*-auth-event$/i, // Last auth event metadata
      /^sb-.*-refresh-token$/i, // Refresh token (if present in some setups)
    ];

    Object.keys(localStorage).forEach((key) => {
      if (patterns.some((re) => re.test(key))) {
        try {
          localStorage.removeItem(key);
        } catch {
          // Silently ignore if key doesn't exist
        }
      }
    });
  } catch {
    // Silently ignore localStorage access errors
  }
}

class AuthClient {
  private isObfuscatedExistingUserSignUp(authData: {
    session: unknown | null;
    user: {
      identities?: unknown[] | null;
    } | null;
  }): boolean {
    return (
      !authData.session &&
      !!authData.user &&
      Array.isArray(authData.user.identities) &&
      authData.user.identities.length === 0
    );
  }

  private async sendExistingAccountEmailLink(email: string): Promise<void> {
    const redirectTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: redirectTo,
      },
    });

    if (error) {
      throw error;
    }
  }

  private async validateEmailForSignup(email: string): Promise<void> {
    class EmailValidationRejection extends Error {}

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/validate-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        // Fail open -- if the validation endpoint is down, allow signup to proceed
        return;
      }

      const result = await response.json();

      if (!result.allowed) {
        throw new EmailValidationRejection(result.reason || "Unable to verify this email.");
      }
    } catch (error) {
      if (error instanceof EmailValidationRejection) {
        throw error;
      }
      // Fail open on everything else: network errors, JSON parse errors,
      // proxy responses, etc. -- never block signup due to infrastructure issues
    }
  }

  private async getSupabaseToken(): Promise<string | null> {
    return getAccessToken();
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit,
    opts?: { accessToken?: string }
  ): Promise<T> {
    // Prefer provided token to avoid extra Supabase calls when already in an auth callback
    const token = opts?.accessToken ?? (await this.getSupabaseToken());
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Add existing headers
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    if (!response.ok) {
      if (response.status === 401) {
        const authError = new Error("Authentication required");
        reportError(authError, {
          context: "Auth token validation failed",
          tags: { feature: "web-auth", status: "401" },
          extras: { endpoint },
        });
        throw authError;
      }

      const errorText = await response.text().catch(() => "");
      const error = new Error(errorText || `Request failed: ${response.statusText}`);
      reportError(error, {
        context: "Auth API request failed",
        tags: { feature: "web-auth", status: response.status.toString() },
        extras: { endpoint },
      });
      throw error;
    }

    return response.json();
  }

  async signUp(data: SignUpData): Promise<SignUpResponse> {
    // Validate email before calling Supabase to block disposable/invalid addresses
    // and avoid creating orphan auth records.
    await this.validateEmailForSignup(data.email);

    const { data: authData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
    });

    if (error) {
      const isAlreadyRegistered = error.message.toLowerCase().includes("user already registered");
      if (isAlreadyRegistered) {
        try {
          await this.sendExistingAccountEmailLink(data.email);
          return {
            user: null,
            message: "Sign-in link sent to existing account email",
          };
        } catch (otpError) {
          const err =
            otpError instanceof Error
              ? otpError
              : new Error("Failed to send sign-in link for existing account");
          reportError(err, {
            context: "Existing-account email link failed after sign-up error",
            tags: { feature: "web-auth", action: "signup" },
            extras: { email: data.email },
          });
          throw err;
        }
      }

      const err = new Error(error.message);
      reportError(err, {
        context: "Sign up failed",
        tags: { feature: "web-auth", action: "signup" },
      });
      throw err;
    }

    if (!authData.user) {
      const userCreationError = new Error("Failed to create user");
      reportError(userCreationError, {
        context: "User creation returned no user object",
        tags: { feature: "web-auth", action: "signup" },
      });
      throw userCreationError;
    }

    // Supabase can return an obfuscated user when the email already exists via
    // another provider; trigger an explicit email link so the user is not stuck.
    if (this.isObfuscatedExistingUserSignUp(authData)) {
      try {
        await this.sendExistingAccountEmailLink(data.email);
      } catch (otpError) {
        const err =
          otpError instanceof Error
            ? otpError
            : new Error("Failed to send sign-in link for existing account");
        reportError(err, {
          context: "Existing-account email link failed after sign-up",
          tags: { feature: "web-auth", action: "signup" },
          extras: { email: data.email },
        });
        throw err;
      }

      return {
        user: null,
        message: "Sign-in link sent to existing account email",
      };
    }

    // Create/sync user in our database when a session exists.
    if (authData.session) {
      await this.syncUserWithBackend(authData.user, {
        accessToken: authData.session.access_token,
      });
    }

    return {
      user: {
        id: authData.user.id,
        email: authData.user.email || "",
        display_name: authData.user.user_metadata?.display_name || null,
        created_at: authData.user.created_at || new Date().toISOString(),
      },
      message: "User created successfully",
    };
  }

  async signIn(data: SignInData): Promise<AuthResponse> {
    const { data: authData, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      const err = new Error(error.message);
      reportError(err, {
        context: "Sign in failed",
        tags: { feature: "web-auth", action: "signin" },
      });
      throw err;
    }

    if (!authData.user) {
      const credentialsError = new Error("Invalid credentials");
      reportError(credentialsError, {
        context: "Sign in returned no user object",
        tags: { feature: "web-auth", action: "signin" },
      });
      throw credentialsError;
    }

    // Note: Backend sync happens in AuthContext.onAuthStateChange when SIGNED_IN fires

    return {
      user: {
        id: authData.user.id,
        email: authData.user.email || "",
        display_name: authData.user.user_metadata?.display_name || null,
        created_at: authData.user.created_at || new Date().toISOString(),
      },
      message: "Signed in successfully",
    };
  }

  async signInWithGoogle(redirectTo?: string): Promise<void> {
    // If on homepage (with or without hash), redirect to dashboard
    // Otherwise, stay on the current page (without hash to avoid redirect issues)
    const isHomepage = window.location.pathname === "/";
    const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
    const defaultRedirect = isHomepage ? `${window.location.origin}/dashboard` : cleanUrl;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectTo || defaultRedirect,
        queryParams: {
          prompt: "consent",
          access_type: "offline",
        },
      },
    });

    if (error) {
      const oauthError = new Error(error.message);
      reportError(oauthError, {
        context: "Google OAuth sign in failed",
        tags: { feature: "web-auth", action: "oauth", provider: "google" },
      });
      throw oauthError;
    }

    // Note: OAuth redirects to the specified URL, so no return value needed
  }

  async signInWithApple(redirectTo?: string): Promise<void> {
    // If on homepage (with or without hash), redirect to dashboard
    // Otherwise, stay on the current page (without hash to avoid redirect issues)
    const isHomepage = window.location.pathname === "/";
    const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
    const defaultRedirect = isHomepage ? `${window.location.origin}/dashboard` : cleanUrl;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        redirectTo: redirectTo || defaultRedirect,
        queryParams: {
          prompt: "consent",
        },
      },
    });

    if (error) {
      const oauthError = new Error(error.message);
      reportError(oauthError, {
        context: "Apple OAuth sign in failed",
        tags: { feature: "web-auth", action: "oauth", provider: "apple" },
      });
      throw oauthError;
    }

    // Note: OAuth redirects to the specified URL, so no return value needed
  }

  async signInWithGithub(redirectTo?: string): Promise<void> {
    // If on homepage (with or without hash), redirect to dashboard
    // Otherwise, stay on the current page (without hash to avoid redirect issues)
    const isHomepage = window.location.pathname === "/";
    const cleanUrl = window.location.origin + window.location.pathname + window.location.search;
    const defaultRedirect = isHomepage ? `${window.location.origin}/dashboard` : cleanUrl;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: "github",
      options: {
        redirectTo: redirectTo || defaultRedirect,
        scopes: "user:email",
        queryParams: { prompt: "select_account" },
      },
    });

    if (error) {
      const oauthError = new Error(error.message);
      reportError(oauthError, {
        context: "GitHub OAuth sign in failed",
        tags: { feature: "web-auth", action: "oauth", provider: "github" },
      });
      throw oauthError;
    }

    // Note: OAuth redirects to the specified URL, so no return value needed
  }

  async signOut(): Promise<{ message: string; remoteLoggedOut: boolean }> {
    let remoteLoggedOut = true;
    try {
      const { error } = await supabase.auth.signOut({ scope: "local" });
      if (error) {
        remoteLoggedOut = false;
        throw new Error(error.message);
      }
    } catch (error) {
      remoteLoggedOut = false;
      const message = (error as { message?: string })?.message || "Sign out from Supabase failed";
      const err = error instanceof Error ? error : new Error(message);
      reportError(err, {
        context: "Sign out from Supabase failed",
        tags: { feature: "web-auth", action: "signout" },
      });
      // Clear locally if remote sign-out fails
      clearLocalAuth();
    } finally {
      // Ensure any app-specific remnants are cleared regardless
      clearLocalAuth();
      clearAccessToken();
    }
    return {
      message: remoteLoggedOut
        ? "Signed out successfully"
        : "Signed out locally; remote sign-out failed",
      remoteLoggedOut,
    };
  }

  async getSession(): Promise<UserProfile | null> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        return null;
      }

      return {
        id: session.user.id,
        email: session.user.email || "",
        display_name: session.user.user_metadata?.display_name || null,
        created_at: session.user.created_at || new Date().toISOString(),
      };
    } catch (error) {
      const message = (error as { message?: string })?.message || "Auth session check failed";
      const err = error instanceof Error ? error : new Error(message);
      reportError(err, {
        context: "Failed to fetch Supabase session",
        tags: { feature: "web-auth", action: "get-session" },
      });
      return null;
    }
  }

  async getCurrentUser(): Promise<UserProfile> {
    const user = await this.getSession();
    if (!user) {
      throw new Error("Not authenticated");
    }
    return user;
  }

  async syncUserWithBackend(
    user: {
      id: string;
      email?: string | null;
      user_metadata?: { display_name?: string | null };
    },
    opts?: { accessToken?: string }
  ): Promise<void> {
    // Skip sync if no email (shouldn't happen in practice, but be defensive)
    if (!user.email) {
      console.warn("[authClient] Cannot sync user without email");
      return;
    }

    try {
      // This endpoint will create or update the user in our backend database
      await this.request(
        "/api/v1/auth/sync-user",
        {
          method: "POST",
          body: JSON.stringify({
            id: user.id,
            email: user.email,
            display_name: user.user_metadata?.display_name || null,
          }),
        },
        {
          accessToken: opts?.accessToken,
        }
      );
    } catch (error) {
      console.warn("Failed to sync user with backend:", error);
      // Don't throw here - auth can still work without backend sync
    }
  }
}

export const authClient = new AuthClient();
